import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import getCopyWithParams from 'utils/getCopyWithParams';
import Store from 'store/store';
import * as oneTrust from 'utils/gdpr/oneTrust';
import s from 'components/bolt/youtube/youtube.sss';

class Youtube extends Component {

	constructor(props) {
		super(props);
		this.progressTracking = {};
		this.updateInstanceCounter = 0;
		this.started = false;
		this.getProgress = this.getProgress.bind(this);
		this.onPlayerReady = this.onPlayerReady.bind(this);
		this.onPlayerStateChange = this.onPlayerStateChange.bind(this);
		this.pause = this.pause.bind(this);

		let appData = Store.get().appData;
		this.usesEvidon = appData?.options?.requireUserConsent?.companyId;
		this.usesOneTrust = appData?.options?.oneTrustCookieConsent?.scripts || appData?.options?.oneTrustCookieConsent?.configuredFromExternalScript;
		this.usesPowCookieBanner = appData?.options?.cookiesModal?.enableModal;
		this.usesKetch = appData?.options?.ketchCookieConsent?.scripts;
		this.usesDidomi = appData?.options?.didomiCookieConsent?.scripts;
		this.usesCookieYes = appData?.options?.cookieYesConsent?.scripts;
		this.needsCookieCheck = this.usesEvidon
			|| this.usesOneTrust
			|| this.usesKetch
			|| this.usesDidomi
			|| this.usesPowCookieBanner
			|| this.usesCookieYes;
		if (this.props.noCookies) {
			this.usesOneTrust = false;
			this.usesPowCookieBanner = false;
			this.needsCookieCheck = false;
		}
		Store.on('PAUSE_ALL_VIDEOS', this.pause);
	}

	componentDidMount() {
		this.instanceId = Math.random().toString().replace('.', '');
		this.blocked = this.shouldBlockRender();
		if (!this.blocked) {
			this.initYoutube();
		} else {
			this.props.onPlayerReady();
		}
	}

	componentDidUpdate(prevProps) {
		const wasBlockedBefore = this.blocked;
		this.blocked = this.shouldBlockRender();

		// blocked -> unblocked (or video change while unblocked)
		const videoIdChanged = prevProps.videoId !== this.props.videoId;
		if (!this.blocked && (wasBlockedBefore || videoIdChanged)) {
			this.initYoutube();
		}

		// unblocked -> blocked
		if (this.blocked && !wasBlockedBefore && this.player) {
			this.player.destroy();
		}
	}

	componentWillUnmount() {
		clearInterval(this.progressUpdate);
		clearInterval(this.readyTimeout);
		this.unmounted = true;
		let target = document.querySelector(`.elementForYTiframe${this.instanceId}`);
		if (target) {
			target.remove();
		}
		if (this.player) {
			this.player.destroy();
		}
		Store.off('PAUSE_ALL_VIDEOS', this.pause);
	}

	shouldBlockRender() {
		return (this.needsCookieCheck && !this.state.oneTrustAllowed) || Store.get().ageGateOpen;
	}

	play() {
		if (this.player && this.player.playVideo) {
			this.player.playVideo();
		}
	}

	pause() {
		if (this.player && this.player.pauseVideo) {
			this.player.pauseVideo();
		}
	}

	get duration() {
		if (this.player && this.player.getDuration) {
			return this.player.getDuration();
		}
		return 0;
	}

	initYoutube() {
		const { videoId } = this.props;
		if (typeof window === 'undefined') {
			return;
		}
		if (window.YT && (window.YT.Player || window.YT.loaded === 0)) {
			return window.YT.ready(() => this.loadPlayer(videoId));
		}
		if (!window.YTLoader) {
			window.YTLoader = new Promise(resolve => {
				// window.onYouTubeIframeAPIReady = () => setTimeout(resolve, 0);
				window.onYouTubeIframeAPIReady = () => window.YT.ready(resolve);
				const tag = document.createElement('script');
				tag.src = 'https://www.youtube.com/iframe_api';
				document.body.appendChild(tag);
			});
		}

		let instanceCounter = ++this.updateInstanceCounter;

		return window.YTLoader.then(() => {
			if (this.unmounted || instanceCounter !== this.updateInstanceCounter) {
				return;
			}
			this.loadPlayer(videoId);
		});
	}

	loadPlayer(videoId) {
		if (!videoId || !this.$ytPlayer) {
			return;
		}

		let target = document.querySelector(`.elementForYTiframe${this.instanceId}`);
		if (!target) {
			target = document.createElement('div');
			target.setAttribute('key', videoId);
			target.setAttribute('class', `elementForYTiframe${this.instanceId}`);
			this.$ytPlayer.appendChild(target);
		}

		let ready = false;
		const onReady = () => {
			if (ready) return;
			ready = true;
			this.onPlayerReady();
		};
		// We sometimes don't get the ready event for some reason - add a timeout
		clearTimeout(this.readyTimeout);
		this.readyTimeout = setTimeout(onReady, 5000);
		this.player = new window.YT.Player(target, {
			height: '100%',
			width: '100%',
			videoId: videoId,
			host: this.props.noCookies && 'https://www.youtube-nocookie.com',
			events: {
				onReady: onReady,
				onStateChange: this.onPlayerStateChange
			},
			playerVars: {
				rel: 0,
				enablejsapi: 1
			}
		});
	}

	onPlayerReady() {
		const { autoplay, muted } = this.props;
		this.progressUpdate = setInterval(this.getProgress, 50);
		if (autoplay) {
			this.player?.playVideo?.();
		}
		if (typeof this.props.onPlayerReady === 'function') {
			this.props.onPlayerReady();
		}
		if (muted) {
			this.player?.mute();
		} else {
			this.player?.unMute?.();
		}
	}

	getProgress() {
		if (!this.player?.getCurrentTime) {
			return;
		}
		const currentTime = this.player.getCurrentTime();
		const videoLength = this.player.getDuration();
		const progress = currentTime / videoLength;

		let step = [0, 25, 50, 75, 95, 100].find(step => progress >= (step / 100) && !this.progressTracking[step]);
		if (step || step === 0) {
			this.progressTracking[step] = true;
			const videoData = this.player.getVideoData();

			Store.emit('VIDEO_PLAYER_PROGRESS', {
				platform: 'youtube',
				video: this.props.videoId,
				step,
				time: currentTime,
				videoLength,
				isShowtimesMessage: this.props.isShowtimesMessage,
				isTrailerOverlay: this.props.isTrailerOverlay,
				isTrailer: this.props.isTrailer,
				videoTitle: videoData?.title
			});
		}

		if (typeof this.props.onProgress === 'function') {
			this.props.onProgress(progress, currentTime, videoLength);
		}
	}

	onPlayerStateChange(e) {
		const { onEnded, onStart, onPause, onPlay, onStateChange } = this.props;
		if (typeof onStateChange === 'function') {
			onStateChange(e);
		}
		if (e.data === 0 && typeof onEnded === 'function') {
			onEnded();
		}
		if (e.data === 1) {
			if (!this.started && typeof onStart === 'function') {
				onStart();
			}
			if (typeof onPlay === 'function') {
				onPlay();
			}
			this.started = true;
		}
		if (e.data === 2) {
			if (typeof onPause === 'function') {
				onPause();
			}
		}
	}

	getShowCookieSettingsFunction() {
		if (this.usesEvidon) return () => window.evidon.notice.showPreferencesDialog();
		if (this.usesOneTrust) return () => Store.emit('ONETRUST_SHOW');
		if (this.usesPowCookieBanner) return () => Store.emit('COOKIE_PREFERENCES_OPEN');
		if (this.usesDidomi) return () => window.Didomi.preferences.show();
	}


	renderBlocked() {
		const copy = Store.get().appData?.copy?.baseCopy;
		let prompt = (this.props.noCookies && copy.$VIDEOS_COOKIES_FUNCTIONAL) || copy.$VIDEOS_COOKIES;
		let showCookieSettings = this.getShowCookieSettingsFunction();

		return (
			<div class={joinClasses(s.wrapper, this.props.responsiveResize && s.responsiveResize)} key="blocked-wrapper">
				<div class={s.prompt}>{prompt}</div>
				{showCookieSettings && <button id="youtubeCookieSettings" class={s.cta} onClick={showCookieSettings}>{getCopyWithParams( copy, copy.$VIDEOS_COOKIES_SETTINGS )}</button>}
			</div>
		);
	}
	renderPlayer() {
		return <div className={this.props.responsiveResize && s.responsiveResize} ref={$ref => this.$ytPlayer = $ref} key="player" />;
	}
	render() {
		return this.shouldBlockRender() ? this.renderBlocked() : this.renderPlayer();
	}
}

export default oneTrust.connectToOneTrust(props => props.disableCookieCheck || (props.noCookies ? oneTrust.FUNCTIONAL : oneTrust.TARGETING), Youtube);
